import React from 'react';
import { useDispatch } from 'react-redux';

import { ResponsiveDialog, useFilters, useSettings, useUserData, Utils } from 'app/main/components';
import ImportFromRowsPreviewDialog from '../../components/ImportFromRowsPreviewDialog';
import DemsRecipientsImportFiltersDialog from './DemsRecipientsImportFiltersDialog';

interface DemsRecipientsImportDialogProps {
	openDialog: boolean;
	switchOpenDialog: VoidFunction;
	demsUid: string;
	table: string;
	updateRefresh: VoidFunction;
}

export default function DemsRecipientsImportDialog(props: DemsRecipientsImportDialogProps) {
	const dispatch = useDispatch();
	const userData = useUserData();

	const [srcModule, setSrcModule] = React.useState('marketingactions');

	const { settings, t } = useSettings(srcModule);
	const { settings: tableSettings } = useSettings(props.table);

	const [srcUid, setSrcUid] = React.useState<string | undefined>();
	const [includePeople, setIncludePeople] = React.useState<boolean | null>(false);
	const [showFilteredResults, setShowFilteredResults] = React.useState(false);

	const srcModuleRows = srcModule === 'dems' ? `${srcModule}_recipients` : `${srcModule}_rows`;
	const srcModuleShortRows = srcModule === 'commercialcampaigns' ? 'cmrw' : srcModule === 'dems' ? 'mail' : 'marw';

	const filterProps = useFilters({
		module: srcModuleRows,
		defaultFilters: [
			{ filt_field: `${tableSettings.modu_short}_deleted`, filt_clause: 'not_equal', filt_value: 1 },
			{ filt_field: `${settings.modu_short}_uid`, filt_clause: 'equals', filt_value: srcUid },
			{ filt_field: `${srcModuleShortRows}_table`, filt_clause: 'equals', filt_value: props.table }
		]
	});

	const showResults = () => {
		filterProps.handleFiltersApply();
		setShowFilteredResults(true);
	};

	const closeDialog = () => {
		filterProps.handleFiltersClear();
		props.switchOpenDialog();
		setShowFilteredResults(false);
	};

	const addRecipient = () => {
		Utils.executeStored({
			action: 'st_dems_recipients_add_multiple',
			dispatch,
			data: {
				derc_dems_uid: props.demsUid,
				derc_user_created_uid: userData.user_uid,
				src_module: srcModule,
				src_module_uid: srcUid,
				include_associated_people: includePeople ? 1 : 0,
				filters: filterProps.filters
			},
			successHandler: () => {
				dispatch(Utils.alertSuccessMsg(t));
				setSrcUid(undefined);
				setIncludePeople(false);
				closeDialog();
				props.updateRefresh();
			}
		});
	};

	if (!showFilteredResults)
		return (
			<ResponsiveDialog open={props.openDialog} onClose={props.switchOpenDialog} maxWidth="lg" scroll="body">
				<DemsRecipientsImportFiltersDialog
					uid={props.demsUid}
					table={props.table}
					srcModule={srcModule}
					setSrcModule={setSrcModule}
					srcUid={srcUid}
					setSrcUid={setSrcUid}
					defaultFilters={filterProps.defaultFilters}
					currentFilters={filterProps.currentFilters}
					handleFilterChange={filterProps.handleFilterChange}
					setCurrentFilters={filterProps.setCurrentFilters}
					includePeople={includePeople}
					setIncludePeople={setIncludePeople}
					showResults={showResults}
					closeDialog={closeDialog}
				/>
			</ResponsiveDialog>
		);
	else
		return (
			<ResponsiveDialog open={props.openDialog} onClose={props.switchOpenDialog} maxWidth="lg">
				<ImportFromRowsPreviewDialog
					title={t('DEMS_DIALOG_TITLE_ADD_RESULT')}
					module={srcModuleRows}
					defaultFilters={filterProps.filters}
					addRows={addRecipient}
					goBack={() => setShowFilteredResults(false)}
				/>
			</ResponsiveDialog>
		);
}
